import React, { useState, useEffect, useMemo } from 'react';
import { Save, Calculator, RefreshCw, Info, Copy, Layers, Check, X, Calendar, ChevronDown, Edit2, Settings } from 'lucide-react';
import { apiFetch } from '../utils/api';
import ScoringStrategyManager from './ScoringStrategyManager';

/**
 * ScoringSettings - Club Admin component for viewing/managing scoring
 * 
 * Features:
 * - View current scoring configuration (platform default or custom)
 * - Optionally create custom scoring based on platform default
 * - Preview/test scoring calculations
 */

// Client-side scoring calculation for preview
function calculateScorePreview(config, playerCount, rank) {
    if (rank < 1 || playerCount < 1) return 0;
    
    // New config format with ranges
    if (config?.ranges) {
        const range = config.ranges.find(r => {
            const minOk = playerCount >= r.min_players;
            const maxOk = r.max_players === null || playerCount <= r.max_players;
            return minOk && maxOk;
        });
        
        if (!range) return 0;
        
        if (range.strategy_type === 'table' && range.tableRules) {
            // Convert array to object if needed
            const rules = Array.isArray(range.tableRules) 
                ? range.tableRules.reduce((acc, r) => {
                    if (!acc[r.player_count]) acc[r.player_count] = {};
                    acc[r.player_count][r.rank] = r.points;
                    return acc;
                  }, {})
                : range.tableRules;
            return rules[playerCount]?.[rank] ?? 0;
        }
        
        if (range.strategy_type === 'formula' && range.formula) {
            const vars = range.formula.variables || {};
            const multiplier = vars.multiplier || 2;
            const pot = playerCount * multiplier;
            
            if (rank === 1) return Math.floor(pot * (vars.rank1Percent || 0.5));
            if (rank === 2) return Math.floor(pot * (vars.rank2Percent || 0.3));
            if (rank === 3) return Math.floor(pot * (vars.rank3Percent || 0.2));
            return 0;
        }
        
        return 0;
    }
    
    // Legacy config format
    const scoringTable = config?.scoringTable || {};
    const fallback = config?.fallback || {
        multiplier: 2,
        rank1Percent: 0.5,
        rank2Percent: 0.3,
        rank3Percent: 0.2,
        minPlayersForPoints: 2
    };
    
    if (scoringTable[playerCount]) {
        const points = scoringTable[playerCount][rank];
        return points !== undefined ? points : 0;
    }

    if (playerCount < fallback.minPlayersForPoints) return 0;

    const basePot = playerCount * fallback.multiplier;
    
    if (rank === 1) {
        return Math.floor(basePot * fallback.rank1Percent);
    } else if (rank === 2) {
        return Math.floor(basePot * fallback.rank2Percent);
    } else if (rank === 3) {
        return Math.floor(basePot * fallback.rank3Percent);
    } else {
        return 0;
    }
}

const ScoringSettings = () => {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hasCustom, setHasCustom] = useState(false);
    const [creatingCustom, setCreatingCustom] = useState(false);
    const [message, setMessage] = useState(null);
    const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'strategies' | 'seasons'

    // Sandbox state
    const [testPlayers, setTestPlayers] = useState(4);
    const [testRank, setTestRank] = useState(1);
    
    // Seasons state
    const [seasons, setSeasons] = useState([]);
    const [strategies, setStrategies] = useState([]);
    const [editingSeasonId, setEditingSeasonId] = useState(null);
    const [seasonStrategySelect, setSeasonStrategySelect] = useState({});
    
    // Strategy management
    const [allStrategies, setAllStrategies] = useState([]);
    const [cloningStrategy, setCloningStrategy] = useState(null);
    const [cloneName, setCloneName] = useState('');
    
    // Delete strategy state
    const [deletingStrategy, setDeletingStrategy] = useState(null);
    const [replacementOptions, setReplacementOptions] = useState([]);
    const [selectedReplacement, setSelectedReplacement] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    
    // Edit strategy state
    const [editingStrategy, setEditingStrategy] = useState(null);

    useEffect(() => {
        fetchConfig();
        fetchSeasons();
        fetchStrategies();
        fetchAllStrategies();
    }, []);

    const fetchConfig = async () => {
        setLoading(true);
        try {
            // Check if club has custom scoring
            const customRes = await apiFetch('/api/scoring/has-custom');
            if (customRes.ok) {
                const { hasCustom: custom } = await customRes.json();
                setHasCustom(custom);
            }
            
            // Get scoring config (custom or platform default)
            const res = await apiFetch('/api/scoring/config');
            if (res.ok) {
                const data = await res.json();
                setConfig(data);
            } else {
                setMessage({ type: 'error', text: 'Pisteytysasetusten lataus epäonnistui' });
            }
        } catch (err) {
            console.error('Failed to fetch scoring config:', err);
            setMessage({ type: 'error', text: 'Verkkovirhe' });
        } finally {
            setLoading(false);
        }
    };

    const fetchSeasons = async () => {
        try {
            const res = await apiFetch('/api/seasons');
            if (res.ok) {
                const data = await res.json();
                setSeasons(data);
            }
        } catch (err) {
            console.error('Failed to fetch seasons:', err);
        }
    };

    const fetchStrategies = async () => {
        try {
            const res = await apiFetch('/api/seasons/strategies');
            if (res.ok) {
                const data = await res.json();
                setStrategies(data);
            }
        } catch (err) {
            console.error('Failed to fetch strategies:', err);
        }
    };

    const fetchAllStrategies = async () => {
        try {
            const res = await apiFetch('/api/scoring/strategies');
            if (res.ok) {
                const data = await res.json();
                setAllStrategies(data);
            }
        } catch (err) {
            console.error('Failed to fetch all strategies:', err);
        }
    };

    const cloneStrategy = async () => {
        if (!cloningStrategy) return;
        
        setCreatingCustom(true);
        try {
            const res = await apiFetch('/api/scoring/clone', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    source_strategy_id: cloningStrategy.id,
                    new_name: cloneName || `${cloningStrategy.name} (kopio)`
                })
            });
            
            if (res.ok) {
                setMessage({ type: 'success', text: 'Strategia kopioitu!' });
                setCloningStrategy(null);
                setCloneName('');
                fetchAllStrategies();
                fetchConfig();
            } else {
                const data = await res.json();
                setMessage({ type: 'error', text: data.error || 'Kopiointi epäonnistui' });
            }
        } catch (err) {
            console.error('Clone failed:', err);
            setMessage({ type: 'error', text: 'Verkkovirhe' });
        } finally {
            setCreatingCustom(false);
        }
    };

    const initiateDelete = async (strategy) => {
        setDeleteLoading(true);
        try {
            // Try to delete - will fail if in use
            const res = await apiFetch(`/api/scoring/strategies/${strategy.id}`, {
                method: 'DELETE'
            });
            
            if (res.ok) {
                setMessage({ type: 'success', text: 'Strategia poistettu!' });
                fetchAllStrategies();
                return;
            }
            
            const data = await res.json();
            
            if (data.requiresReplacement) {
                // Strategy is in use - show replacement dialog
                setDeletingStrategy({
                    ...strategy,
                    seasonsUsing: data.seasonsUsing,
                    seasonsUsingCount: data.seasonsUsingCount
                });
                setReplacementOptions(data.replacementOptions || []);
                if (data.replacementOptions?.length > 0) {
                    setSelectedReplacement(data.replacementOptions[0].id);
                }
            } else {
                setMessage({ type: 'error', text: data.error || 'Poisto epäonnistui' });
            }
        } catch (err) {
            console.error('Delete failed:', err);
            setMessage({ type: 'error', text: 'Verkkovirhe' });
        } finally {
            setDeleteLoading(false);
        }
    };

    const confirmDelete = async () => {
        if (!deletingStrategy || !selectedReplacement) return;
        
        setDeleteLoading(true);
        try {
            const res = await apiFetch(`/api/scoring/strategies/${deletingStrategy.id}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ replace_with_strategy_id: selectedReplacement })
            });
            
            if (res.ok) {
                setMessage({ type: 'success', text: 'Strategia poistettu ja kaudet päivitetty!' });
                setDeletingStrategy(null);
                setReplacementOptions([]);
                setSelectedReplacement(null);
                fetchAllStrategies();
                fetchSeasons();
            } else {
                const data = await res.json();
                setMessage({ type: 'error', text: data.error || 'Poisto epäonnistui' });
            }
        } catch (err) {
            console.error('Confirm delete failed:', err);
            setMessage({ type: 'error', text: 'Verkkovirhe' });
        } finally {
            setDeleteLoading(false);
        }
    };

    const updateSeasonStrategy = async (seasonId, strategyId) => {
        try {
            const res = await apiFetch(`/api/seasons/${seasonId}/strategy`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ strategy_id: strategyId })
            });
            
            if (res.ok) {
                setMessage({ type: 'success', text: 'Kauden pisteytys päivitetty!' });
                setTimeout(() => setMessage(null), 3000);
                setEditingSeasonId(null);
                fetchSeasons();
            } else {
                const data = await res.json();
                setMessage({ type: 'error', text: data.error || 'Päivitys epäonnistui' });
            }
        } catch (err) {
            console.error('Failed to update season strategy:', err);
            setMessage({ type: 'error', text: 'Verkkovirhe' });
        }
    };

    const createCustomScoring = async () => {
        setCreatingCustom(true);
        setMessage(null);
        try {
            const res = await apiFetch('/api/scoring/create-custom', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            
            if (res.ok) {
                setMessage({ type: 'success', text: 'Mukautettu pisteytys luotu!' });
                setHasCustom(true);
                fetchConfig();
            } else {
                const data = await res.json();
                setMessage({ type: 'error', text: data.error || 'Luonti epäonnistui' });
            }
        } catch (err) {
            console.error('Failed to create custom scoring:', err);
            setMessage({ type: 'error', text: 'Verkkovirhe' });
        } finally {
            setCreatingCustom(false);
        }
    };

    // Live calculated preview
    const previewPoints = useMemo(() => {
        if (!config) return 0;
        return calculateScorePreview(config, testPlayers, testRank);
    }, [testPlayers, testRank, config]);

    // Generate preview table
    const previewTable = useMemo(() => {
        if (!config) return {};
        const table = {};
        for (let players = 2; players <= 10; players++) {
            table[players] = {};
            for (let rank = 1; rank <= Math.min(players, 8); rank++) {
                table[players][rank] = calculateScorePreview(config, players, rank);
            }
        }
        return table;
    }, [config]);

    if (loading) {
        return (
            <div className="loading-container" style={{ padding: '2rem', textAlign: 'center' }}>
                <RefreshCw size={32} className="spinning" />
                <p>Ladataan pisteytysasetuksia...</p>
            </div>
        );
    }

    return (
        <div className="scoring-settings">
            <div className="card">
                <h2><Layers size={24} /> Pisteytysasetukset</h2>
                
                {message && (
                    <div className={`message ${message.type}`} style={{
                        padding: '0.75rem',
                        marginBottom: '1rem',
                        borderRadius: '4px',
                        background: message.type === 'success' ? '#d4edda' : '#f8d7da',
                        color: message.type === 'success' ? '#155724' : '#721c24',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}>
                        {message.type === 'success' ? <Check size={16} /> : <X size={16} />}
                        {message.text}
                    </div>
                )}

                {/* Tabs */}
                <div style={{ 
                    display: 'flex', 
                    gap: '0.5rem', 
                    marginBottom: '1.5rem',
                    borderBottom: '2px solid #e0e0e0',
                    paddingBottom: '0.5rem'
                }}>
                    <button 
                        onClick={() => setActiveTab('overview')}
                        style={{
                            padding: '0.5rem 1rem',
                            border: 'none',
                            background: activeTab === 'overview' ? '#1976d2' : 'transparent',
                            color: activeTab === 'overview' ? 'white' : '#666',
                            borderRadius: '4px 4px 0 0',
                            cursor: 'pointer',
                            fontWeight: activeTab === 'overview' ? 600 : 400
                        }}
                    >
                        Yleiskatsaus
                    </button>
                    <button 
                        onClick={() => setActiveTab('strategies')}
                        style={{
                            padding: '0.5rem 1rem',
                            border: 'none',
                            background: activeTab === 'strategies' ? '#1976d2' : 'transparent',
                            color: activeTab === 'strategies' ? 'white' : '#666',
                            borderRadius: '4px 4px 0 0',
                            cursor: 'pointer',
                            fontWeight: activeTab === 'strategies' ? 600 : 400
                        }}
                    >
                        Strategiat ({allStrategies.filter(s => s.is_owned).length})
                    </button>
                    <button 
                        onClick={() => setActiveTab('seasons')}
                        style={{
                            padding: '0.5rem 1rem',
                            border: 'none',
                            background: activeTab === 'seasons' ? '#1976d2' : 'transparent',
                            color: activeTab === 'seasons' ? 'white' : '#666',
                            borderRadius: '4px 4px 0 0',
                            cursor: 'pointer',
                            fontWeight: activeTab === 'seasons' ? 600 : 400
                        }}
                    >
                        <Calendar size={14} style={{ marginRight: '0.25rem' }} />
                        Kaudet ({seasons.length})
                    </button>
                </div>

                {/* Strategies Tab */}
                {activeTab === 'strategies' && (
                    <div style={{ marginBottom: '2rem' }}>
                        <h3>Klubin pisteytysstrategiat</h3>
                        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
                            Luo uusi strategia kopioimalla mallipohja tai oma strategia. Mallipohjia ei voi muokata, mutta ne voi ottaa käyttöön.
                        </p>
                        
                        {/* Own strategies */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h4 style={{ color: '#333', borderBottom: '1px solid #e0e0e0', paddingBottom: '0.5rem' }}>
                                Omat strategiat
                            </h4>
                            {allStrategies.filter(s => s.is_owned).length === 0 ? (
                                <p style={{ color: '#999', fontStyle: 'italic' }}>
                                    Ei omia strategioita. Luo uusi kopioimalla mallipohja.
                                </p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    {allStrategies.filter(s => s.is_owned).map(s => (
                                        <div key={s.id} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '0.75rem 1rem',
                                            background: '#f8f9fa',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '8px'
                                        }}>
                                            <div>
                                                <strong>{s.name}</strong>
                                                {s.seasons_using > 0 && (
                                                    <span style={{ 
                                                        marginLeft: '0.5rem',
                                                        fontSize: '0.8rem',
                                                        color: '#666'
                                                    }}>
                                                        ({s.seasons_using} kautta)
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <button 
                                                    className="btn-primary"
                                                    style={{ fontSize: '0.8rem', padding: '0.4rem 0.75rem' }}
                                                    onClick={() => setEditingStrategy(s)}
                                                >
                                                    <Settings size={14} /> Muokkaa
                                                </button>
                                                <button 
                                                    className="btn-secondary"
                                                    style={{ fontSize: '0.8rem', padding: '0.4rem 0.75rem' }}
                                                    onClick={() => {
                                                        setCloningStrategy(s);
                                                        setCloneName(`${s.name} (kopio)`);
                                                    }}
                                                >
                                                    <Copy size={14} /> Kopioi
                                                </button>
                                                <button 
                                                    className="btn-secondary"
                                                    style={{ 
                                                        fontSize: '0.8rem', 
                                                        padding: '0.4rem 0.75rem',
                                                        color: '#c0392b',
                                                        borderColor: '#c0392b'
                                                    }}
                                                    onClick={() => initiateDelete(s)}
                                                    disabled={deleteLoading}
                                                >
                                                    <X size={14} /> Poista
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        {/* Templates */}
                        <div>
                            <h4 style={{ color: '#333', borderBottom: '1px solid #e0e0e0', paddingBottom: '0.5rem' }}>
                                Mallipohjat (vain luku)
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {allStrategies.filter(s => s.is_readonly && !s.is_owned).map(s => (
                                    <div key={s.id} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '0.75rem 1rem',
                                        background: '#fff3e0',
                                        border: '1px solid #ffcc80',
                                        borderRadius: '8px'
                                    }}>
                                        <div>
                                            <strong>{s.name}</strong>
                                            <span style={{ 
                                                marginLeft: '0.5rem',
                                                fontSize: '0.75rem',
                                                background: '#ff9800',
                                                color: 'white',
                                                padding: '0.15rem 0.5rem',
                                                borderRadius: '4px'
                                            }}>
                                                MALLI
                                            </span>
                                        </div>
                                        <button 
                                            className="btn-secondary"
                                            style={{ fontSize: '0.8rem', padding: '0.4rem 0.75rem' }}
                                            onClick={() => {
                                                setCloningStrategy(s);
                                                setCloneName(s.name);
                                            }}
                                        >
                                            <Copy size={14} /> Kopioi käyttöön
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        {/* Clone Modal */}
                        {cloningStrategy && (
                            <div style={{
                                position: 'fixed',
                                top: 0, left: 0, right: 0, bottom: 0,
                                background: 'rgba(0,0,0,0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 10000
                            }} onClick={() => setCloningStrategy(null)}>
                                <div style={{
                                    background: 'white',
                                    padding: '1.5rem',
                                    borderRadius: '12px',
                                    maxWidth: '400px',
                                    width: '90%'
                                }} onClick={e => e.stopPropagation()}>
                                    <h3 style={{ margin: '0 0 1rem' }}>Kopioi strategia</h3>
                                    <p style={{ color: '#666', marginBottom: '1rem' }}>
                                        Kopioidaan: <strong>{cloningStrategy.name}</strong>
                                    </p>
                                    <div className="form-group">
                                        <label>Uuden strategian nimi</label>
                                        <input 
                                            type="text"
                                            value={cloneName}
                                            onChange={e => setCloneName(e.target.value)}
                                            placeholder="Anna nimi"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                                        <button 
                                            className="btn-primary"
                                            onClick={cloneStrategy}
                                            disabled={creatingCustom || !cloneName.trim()}
                                        >
                                            {creatingCustom ? <RefreshCw size={14} className="spinning" /> : <Copy size={14} />}
                                            {' '}Kopioi
                                        </button>
                                        <button 
                                            className="btn-secondary"
                                            onClick={() => setCloningStrategy(null)}
                                        >
                                            Peruuta
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Delete Confirmation Modal */}
                        {deletingStrategy && (
                            <div style={{
                                position: 'fixed',
                                top: 0, left: 0, right: 0, bottom: 0,
                                background: 'rgba(0,0,0,0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 10000
                            }} onClick={() => setDeletingStrategy(null)}>
                                <div style={{
                                    background: 'white',
                                    padding: '1.5rem',
                                    borderRadius: '12px',
                                    maxWidth: '450px',
                                    width: '90%'
                                }} onClick={e => e.stopPropagation()}>
                                    <h3 style={{ margin: '0 0 1rem', color: '#c0392b' }}>Poista strategia</h3>
                                    
                                    <div style={{
                                        background: '#fff3cd',
                                        border: '1px solid #ffcc00',
                                        padding: '1rem',
                                        borderRadius: '8px',
                                        marginBottom: '1rem'
                                    }}>
                                        <strong>Varoitus!</strong>
                                        <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem' }}>
                                            Strategia "<strong>{deletingStrategy.name}</strong>" on käytössä {deletingStrategy.seasonsUsingCount} kaudella.
                                            Valitse korvaava strategia ennen poistamista.
                                        </p>
                                    </div>
                                    
                                    <div className="form-group">
                                        <label>Korvaava strategia</label>
                                        <select
                                            value={selectedReplacement || ''}
                                            onChange={e => setSelectedReplacement(parseInt(e.target.value))}
                                            style={{ width: '100%' }}
                                        >
                                            {replacementOptions.map(opt => (
                                                <option key={opt.id} value={opt.id}>
                                                    {opt.name} {opt.is_template ? '(malli)' : opt.club_id === null ? '(globaali)' : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    
                                    <p style={{ fontSize: '0.85rem', color: '#666', margin: '0.5rem 0 1rem' }}>
                                        Kaudet, jotka käyttävät tätä strategiaa, päivitetään käyttämään valittua korvaavaa strategiaa.
                                    </p>
                                    
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <button 
                                            className="btn-primary"
                                            style={{ background: '#c0392b' }}
                                            onClick={confirmDelete}
                                            disabled={deleteLoading || !selectedReplacement}
                                        >
                                            {deleteLoading ? <RefreshCw size={14} className="spinning" /> : <X size={14} />}
                                            {' '}Poista strategia
                                        </button>
                                        <button 
                                            className="btn-secondary"
                                            onClick={() => setDeletingStrategy(null)}
                                        >
                                            Peruuta
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Edit Strategy Modal */}
                        {editingStrategy && (
                            <div style={{
                                position: 'fixed',
                                top: 0, left: 0, right: 0, bottom: 0,
                                background: 'rgba(0,0,0,0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 10000,
                                padding: '1rem',
                                overflow: 'auto'
                            }}>
                                <div style={{
                                    background: 'white',
                                    padding: '1.5rem',
                                    borderRadius: '12px',
                                    maxWidth: '900px',
                                    width: '100%',
                                    maxHeight: '90vh',
                                    overflow: 'auto'
                                }}>
                                    <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        marginBottom: '1rem',
                                        borderBottom: '1px solid #e0e0e0',
                                        paddingBottom: '1rem'
                                    }}>
                                        <h3 style={{ margin: 0 }}>
                                            <Settings size={20} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                                            Muokkaa: {editingStrategy.name}
                                        </h3>
                                        <button 
                                            onClick={() => {
                                                setEditingStrategy(null);
                                                fetchAllStrategies();
                                                fetchConfig();
                                            }}
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: '0.5rem'
                                            }}
                                        >
                                            <X size={24} />
                                        </button>
                                    </div>
                                    
                                    <ScoringStrategyManager 
                                        apiPrefix="/api/scoring"
                                        title=""
                                        readOnly={editingStrategy.is_readonly || editingStrategy.is_template}
                                        showTemplatesOnly={false}
                                        initialStrategyId={editingStrategy.id}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Overview Tab - Scoring Source Info */}
                {activeTab === 'overview' && (
                <>
                <div style={{ 
                    background: hasCustom ? '#e8f5e9' : '#e3f2fd',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: hasCustom ? '#4caf50' : '#2196f3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                    }}>
                        {hasCustom ? <Layers size={24} /> : <Info size={24} />}
                    </div>
                    <div style={{ flex: 1 }}>
                        <h3 style={{ margin: 0 }}>
                            {hasCustom ? 'Mukautettu pisteytys' : 'Alustan oletuspisteytys'}
                        </h3>
                        <p style={{ margin: '0.25rem 0 0', color: '#666' }}>
                            {hasCustom 
                                ? 'Klubillasi on käytössä oma mukautettu pisteytys.'
                                : 'Käytössä on alustan oletuspisteytys. Voit luoda oman mukautetun pisteytyksen tarvittaessa.'}
                        </p>
                        {config?.strategyName && (
                            <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem', fontWeight: 500 }}>
                                {config.strategyName}
                            </p>
                        )}
                    </div>
                    
                    {!hasCustom && (
                        <button 
                            className="btn-secondary"
                            onClick={createCustomScoring}
                            disabled={creatingCustom}
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            {creatingCustom ? (
                                <><RefreshCw size={16} className="spinning" /> Luodaan...</>
                            ) : (
                                <><Copy size={16} /> Luo oma pisteytys</>
                            )}
                        </button>
                    )}
                </div>

                {/* Scoring Table Preview */}
                <div style={{ marginBottom: '2rem' }}>
                    <h3>Pistetaulukko</h3>
                    <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
                        <Info size={14} style={{ verticalAlign: 'middle' }} /> Näyttää pisteet kullekin sijoitukselle eri pelaajamäärillä
                    </p>
                    
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                            <thead>
                                <tr style={{ background: '#f5f0e6' }}>
                                    <th style={{ padding: '0.5rem', border: '1px solid #ddd' }}>Pelaajat</th>
                                    {[1, 2, 3, 4, 5, 6, 7, 8].map(rank => (
                                        <th key={rank} style={{ padding: '0.5rem', border: '1px solid #ddd' }}>
                                            {rank}. sija
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(playerCount => (
                                    <tr key={playerCount}>
                                        <td style={{ padding: '0.5rem', border: '1px solid #ddd', fontWeight: 'bold' }}>
                                            {playerCount} pelaajaa
                                        </td>
                                        {[1, 2, 3, 4, 5, 6, 7, 8].map(rank => (
                                            <td key={rank} style={{ 
                                                padding: '0.5rem', 
                                                border: '1px solid #ddd',
                                                textAlign: 'center',
                                                background: rank <= playerCount ? '#fff' : '#f5f5f5'
                                            }}>
                                                {rank <= playerCount ? previewTable[playerCount]?.[rank] ?? 0 : '-'}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Ranges Info */}
                {config?.ranges && (
                    <div style={{ marginBottom: '2rem' }}>
                        <h3>Pisteytysvälit</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {config.ranges.map((range, idx) => (
                                <div key={idx} style={{
                                    padding: '1rem',
                                    background: '#f8f9fa',
                                    borderRadius: '8px',
                                    border: '1px solid #e0e0e0'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: range.strategy_type === 'formula' ? '0.75rem' : 0 }}>
                                        <div style={{ fontWeight: 500 }}>
                                            {range.min_players}-{range.max_players || '∞'} pelaajaa
                                        </div>
                                        <div style={{ 
                                            fontSize: '0.8rem', 
                                            padding: '0.2rem 0.5rem',
                                            borderRadius: '4px',
                                            background: range.strategy_type === 'table' ? '#e3f2fd' : '#fff3e0',
                                            color: range.strategy_type === 'table' ? '#1565c0' : '#e65100'
                                        }}>
                                            {range.strategy_type === 'table' ? '📊 Taulukko' : '📐 Kaava'}
                                        </div>
                                    </div>
                                    
                                    {/* Show formula details */}
                                    {range.strategy_type === 'formula' && range.formula && (
                                        <div style={{ 
                                            background: '#fff',
                                            padding: '0.75rem',
                                            borderRadius: '4px',
                                            border: '1px solid #e0e0e0'
                                        }}>
                                            <div style={{ marginBottom: '0.5rem' }}>
                                                <strong style={{ fontSize: '0.85rem', color: '#666' }}>Kaava:</strong>
                                                <div style={{ 
                                                    fontFamily: 'monospace', 
                                                    fontSize: '0.85rem',
                                                    background: '#f5f5f5',
                                                    padding: '0.5rem',
                                                    borderRadius: '4px',
                                                    marginTop: '0.25rem',
                                                    overflowX: 'auto'
                                                }}>
                                                    {range.formula.expression}
                                                </div>
                                            </div>
                                            
                                            {range.formula.variables && Object.keys(range.formula.variables).length > 0 && (
                                                <div>
                                                    <strong style={{ fontSize: '0.85rem', color: '#666' }}>Muuttujat:</strong>
                                                    <div style={{ 
                                                        display: 'flex', 
                                                        flexWrap: 'wrap', 
                                                        gap: '0.5rem',
                                                        marginTop: '0.25rem'
                                                    }}>
                                                        {Object.entries(range.formula.variables).map(([key, value]) => (
                                                            <span key={key} style={{
                                                                background: '#e8f5e9',
                                                                padding: '0.25rem 0.5rem',
                                                                borderRadius: '4px',
                                                                fontSize: '0.8rem'
                                                            }}>
                                                                {key}: <strong>{value}</strong>
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                </>
                )}

                {/* Seasons Tab */}
                {activeTab === 'seasons' && (
                    <div>
                        <h3><Calendar size={18} /> Kaudet ja pisteytykset</h3>
                        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
                            Jokaiselle kaudelle voidaan valita oma pisteytysstrategia. Kauden vaihtaminen vaikuttaa ko. kauden pistelaskentaan.
                        </p>
                        
                        {seasons.length === 0 ? (
                            <p style={{ color: '#999', fontStyle: 'italic', padding: '2rem', textAlign: 'center' }}>
                                Ei kausia vielä. Kaudet luodaan automaattisesti kun pelisessioita lisätään.
                            </p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {seasons.map(season => (
                                    <div key={season.id} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '0.75rem 1rem',
                                        background: '#f8f9fa',
                                        borderRadius: '8px',
                                        border: '1px solid #e0e0e0',
                                        flexWrap: 'wrap',
                                        gap: '0.5rem'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                            <Calendar size={16} style={{ color: '#666' }} />
                                            <div>
                                                <div style={{ fontWeight: 500 }}>{season.name}</div>
                                                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                                    {new Date(season.season_start).toLocaleDateString('fi-FI')} - {new Date(season.season_end).toLocaleDateString('fi-FI')}
                                                    {season.session_count > 0 && (
                                                        <span style={{ marginLeft: '0.5rem' }}>
                                                            ({season.session_count} sessiota)
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            {editingSeasonId === season.id ? (
                                                <>
                                                    <select 
                                                        value={seasonStrategySelect[season.id] || season.strategy_id || ''}
                                                        onChange={e => setSeasonStrategySelect({
                                                            ...seasonStrategySelect,
                                                            [season.id]: parseInt(e.target.value)
                                                        })}
                                                        style={{ 
                                                            padding: '0.5rem',
                                                            borderRadius: '4px',
                                                            border: '1px solid #ccc'
                                                        }}
                                                    >
                                                        {allStrategies.map(s => (
                                                            <option key={s.id} value={s.id}>
                                                                {s.name} {s.is_template || s.is_readonly ? '(malli)' : s.is_owned ? '' : '(globaali)'}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <button 
                                                        onClick={() => updateSeasonStrategy(
                                                            season.id, 
                                                            seasonStrategySelect[season.id] || season.strategy_id
                                                        )}
                                                        style={{
                                                            background: '#4caf50',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            padding: '0.5rem',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        <Check size={14} />
                                                    </button>
                                                    <button 
                                                        onClick={() => setEditingSeasonId(null)}
                                                        style={{
                                                            background: '#999',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            padding: '0.5rem',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <span style={{
                                                        background: '#e3f2fd',
                                                        color: '#1565c0',
                                                        padding: '0.25rem 0.75rem',
                                                        borderRadius: '4px',
                                                        fontSize: '0.85rem'
                                                    }}>
                                                        {season.strategy_name || 'Ei määritelty'}
                                                    </span>
                                                    <button 
                                                        onClick={() => {
                                                            setEditingSeasonId(season.id);
                                                            setSeasonStrategySelect({
                                                                ...seasonStrategySelect,
                                                                [season.id]: season.strategy_id
                                                            });
                                                        }}
                                                        style={{
                                                            background: 'transparent',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            color: '#666',
                                                            padding: '0.25rem'
                                                        }}
                                                        title="Vaihda pisteytys"
                                                    >
                                                        <Edit2 size={14} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Sandbox / Calculator Preview */}
            <div className="card" style={{ marginTop: '1.5rem', background: '#f0f8ff' }}>
                <h3><Calculator size={20} /> Laskin (Esikatselu)</h3>
                <p style={{ color: '#666', marginBottom: '1rem' }}>
                    Testaa miten nykyiset asetukset vaikuttavat pisteisiin.
                </p>
                
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Pelaajamäärä</label>
                        <input 
                            type="number" 
                            min="2" 
                            max="20"
                            value={testPlayers}
                            onChange={e => setTestPlayers(parseInt(e.target.value) || 2)}
                            style={{ width: '100px' }}
                        />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Sijoitus</label>
                        <input 
                            type="number" 
                            min="1" 
                            max={testPlayers}
                            value={testRank}
                            onChange={e => setTestRank(parseInt(e.target.value) || 1)}
                            style={{ width: '100px' }}
                        />
                    </div>
                    <div style={{ 
                        background: '#d35400', 
                        color: 'white', 
                        padding: '1rem 2rem', 
                        borderRadius: '8px',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>Pisteet</div>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{previewPoints}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScoringSettings;
